import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useHealth } from '../api'
import { ago, formatDuration, stamp } from '../time'
import { Button, CloseButton, Field, QuietButton, Textarea } from '../design'
import * as adm from './admin.css'

function csrf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

type Result = { ok: boolean; message: string }

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'device', label: 'Device' },
  { id: 'content', label: 'Content' },
  { id: 'email', label: 'Email' },
  { id: 'bluesky', label: 'Bluesky' },
  { id: 'broadcast', label: 'Broadcast' },
  { id: 'danger', label: 'Danger zone' },
] as const
type Section = (typeof SECTIONS)[number]['id']

// How the listening state reads at a glance. Freshness keys off the heartbeat, so a genuinely
// quiet night stays "listening" — only a stalled loop goes dark.
const STATUS: Record<string, string> = {
  fresh: 'Listening',
  quiet: 'No signal recently',
  stale: 'Not responding',
  none: 'Nothing heard yet',
}

// Fault `area` → a plain-English name for the broken subsystem. An unknown area (a future recorder)
// falls back to its raw key rather than being dropped, so nothing silently disappears.
const FAULT_LABEL: Record<string, string> = {
  journal_scene: 'Daily journal picture',
  illustration: 'Bird illustration',
  'email:alerts': 'Alert email',
  'email:letter': 'Daily letter email',
  'email:blast': 'Broadcast email',
}
function faultLabel(area: string): string {
  return FAULT_LABEL[area] ?? area
}

// A vital sign, or an em dash. Null from the device means "could not read this", never zero —
// so an unknown must render as visibly absent rather than as a number nobody measured.
function Vital({ label, value, warn }: { label: string; value: string | null; warn?: boolean }) {
  return (
    <li className={adm.rule}>
      <span className={adm.ruleLabel}>{label}</span>
      <span className={warn ? `${adm.ruleValue} ${adm.warn}` : adm.ruleValue}>{value ?? '—'}</span>
    </li>
  )
}

// The device's clock, when it disagrees with ours by enough to matter — null otherwise, so the row
// disappears entirely on a healthy box. Mirrors AdminHealth::CLOCK_DRIFT (5 minutes); the server
// composes the warning sentence, this is just the reading behind it.
const CLOCK_DRIFT_MS = 5 * 60 * 1000

function clockSaid(
  reportedAt: string | null | undefined,
  receivedAt: string | null | undefined,
): string | null {
  if (!reportedAt || !receivedAt) return null
  if (Math.abs(new Date(reportedAt).getTime() - new Date(receivedAt).getTime()) <= CLOCK_DRIFT_MS) return null

  return stamp(reportedAt)
}

// Card space, in ONE unit. The panel used to print GB beside a server-composed warning that said
// the same number in MB ("11.7 GB" and "12000 MB free", one screen apart). GB wins in both; the
// total and the percentage are shown because the model already computes the fraction the warning
// fires on, and "9.2 GB free" alone doesn't say whether that is roomy or nearly out.
function diskFree(
  disk: { free_mb: number | null; total_mb: number | null } | null | undefined,
): string | null {
  if (disk?.free_mb == null) return null
  const free = `${(disk.free_mb / 1024).toFixed(1)} GB`
  if (!disk.total_mb) return free

  const pct = Math.round((disk.free_mb / disk.total_mb) * 100)
  return `${free} of ${(disk.total_mb / 1024).toFixed(1)} GB (${pct}%)`
}

// What the last panel run did, in words. The distinction that matters: "skipped" is healthy —
// the shooter deliberately doesn't burn an e-ink refresh when the station screen is unchanged.
const PANEL_OUTCOME: Record<string, string> = {
  pushed: 'refreshed the panel',
  skipped: 'nothing had changed',
  failed: 'the refresh failed',
}

// --- Bluesky -----------------------------------------------------------------------------
// The three streams the station publishes to one account: the day's journal, a syndicated blog
// note, a priority bird's arrival. Each answers the same three questions in the same order,
// because at the switchover that is the order they get asked in — is it on, what would it say,
// what did it last say.
//
// The shapes mirror Bluesky::Console.snapshot. Previews are the STREAMS' own renderings, passed
// through untouched: nothing in this panel composes a post.

type BskyPreview = {
  text: string
  link: string
  title: string
  description: string
  has_image: boolean
  // Notes only: a note past Bluesky::Notes::MAX_AGE renders exactly like a fresh one but would be
  // recorded as handled rather than posted, so the words alone would mislead.
  stale?: boolean
}
type BskyLedgerRow = {
  ref: string
  date: string | null
  at: string | null
  uri: string | null
  published: boolean
}
type BskyRosterRow = {
  sci: string
  en: string
  ga: string | null
  heard_today: boolean
  last_heard: string | null
  preview: BskyPreview | null
  reason: string | null
}
type BskyStream = {
  name: 'daily' | 'notes' | 'arrivals'
  subject: string | null
  // 'open' | which of the TWO gates is shut. Never collapsed to "off": one is a missing deploy
  // secret and the other an editorial switch, and they are fixed in different files.
  gate: 'open' | 'credentials' | 'switch'
  preview?: BskyPreview | null
  reason?: string | null
  baseline?: boolean
  absence_days?: number
  roster?: BskyRosterRow[]
  ledger: BskyLedgerRow[]
}
type Bsky = {
  // Presence, never a value — the console is told whether the two secrets are set and no more.
  credentials: { handle: boolean; password: boolean }
  streams: BskyStream[]
}

// What each stream is, in the console's own words. The server sends state only; the copy lives
// here with the rest of the panel's writing.
const STREAM_COPY: Record<BskyStream['name'], { label: string; hint: string }> = {
  daily: {
    label: 'The day’s journal',
    hint: 'The hero bird and the opening of the same frozen narration the letter carries, with a link card back to that day’s journal. Run from the nightly sweep.',
  },
  notes: {
    label: 'A blog note, syndicated',
    hint: 'A new note on the blog, carrying its own title and opening, with a card back to the station’s rendering of it. A note more than a week old is recorded as handled rather than posted — only new writing is news.',
  },
  arrivals: {
    label: 'A bird arriving',
    hint: 'A bird from the roster below, heard again after a long silence, posted the same day from the ingest. The post names the species and how long it had been quiet, and nothing else: the station hears, it does not see.',
  },
}

// Which gate is shut, said in the terms of the thing that would open it.
function gateSaid(stream: BskyStream): { text: string; on: boolean } {
  if (stream.gate === 'open') return { text: 'On', on: true }
  if (stream.gate === 'credentials')
    return { text: 'Off — no Bluesky credentials on this station', on: false }

  return { text: `Off — switched off in the station profile (bluesky.streams.${stream.name})`, on: false }
}

function credentialsSaid(c: Bsky['credentials']): { text: string; warn: boolean } {
  if (c.handle && c.password) return { text: 'Handle and app password are both set', warn: false }
  if (c.handle) return { text: 'Handle set; the app password is missing', warn: true }
  if (c.password) return { text: 'App password set; the handle is missing', warn: true }

  return { text: 'Not set here — nothing can be posted from this station', warn: true }
}

// A post as the stream composed it. Shown whole, with its own line breaks: this is the thing
// that would go out, not a description of it.
function Post({ preview }: { preview: BskyPreview }) {
  return (
    <>
      <p className={adm.post}>{preview.text}</p>
      <div className={adm.card}>
        <span className={adm.cardTitle}>{preview.title}</span>
        <span className={adm.cardDesc}>{preview.description}</span>
        <span className={adm.cardLink}>
          {preview.link} —{' '}
          {preview.has_image ? 'a thumbnail was found' : 'no thumbnail found, so a plain card'}
        </span>
      </div>
    </>
  )
}

// What the stream last said. A row with no at:// uri is the stream having LOOKED and decided not
// to post — a birdless day, a note too old to be news — which is a correct outcome, so it is
// never coloured or worded as a failure.
function Ledger({ rows }: { rows: BskyLedgerRow[] }) {
  if (rows.length === 0) return <span className={adm.empty}>Nothing in this stream’s ledger yet.</span>

  return (
    <ul className={adm.ledger}>
      {rows.map((r) => (
        <li key={r.ref} className={adm.ledgerRow}>
          <span className={adm.ledgerRef}>{r.ref}</span>
          <span className={adm.ledgerWhen}>{r.at ? ago(r.at) : (r.date ?? '—')}</span>
          <span className={r.published ? adm.ledgerSaid : adm.ledgerState}>
            {r.published ? 'Posted' : 'Handled — nothing posted'}
          </span>
        </li>
      ))}
    </ul>
  )
}

// The station console — a full-width workspace. Two kinds of thing only: what an admin can do,
// and what fails quietly. Sectioned rather than one long scroll, with Broadcast and the
// destructive Danger zone set apart at the bottom.
export function AdminPanel({ onClose, onBack }: { onClose: () => void; onBack?: () => void }) {
  const qc = useQueryClient()
  const { data: h, isFetching, error } = useHealth(true)
  const [section, setSection] = useState<Section>('overview')
  const [result, setResult] = useState<Result | null>(null)
  const [busy, setBusy] = useState(false)

  const [confirm, setConfirm] = useState('')

  // Removing a species (a BirdNET false positive). Preview shows the counts; the typed DELETE is
  // the real guard, and editing the names after a preview clears it so you can't delete a
  // different set than you previewed.
  const [purgeSpecies, setPurgeSpecies] = useState('')
  const [purgeConfirm, setPurgeConfirm] = useState('')

  // Fixing a single detection: its id, and the scientific name to relabel it to. Removing it
  // outright needs no species — it's the mis-fire no label is right for.
  const [detId, setDetId] = useState('')
  const [detSpecies, setDetSpecies] = useState('')

  // A broadcast is the most dangerous thing in the console, so the send stays locked until the
  // whole ceremony is done: something written, read back as a preview, a copy landed in your
  // own inbox, and the reader count typed out. The server enforces the count too.
  const [blastSubject, setBlastSubject] = useState('')
  const [blastBody, setBlastBody] = useState('')
  const [blastHtml, setBlastHtml] = useState<string | null>(null)
  const [blastTested, setBlastTested] = useState(false)
  const [blastConfirm, setBlastConfirm] = useState('')

  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)
  const [journalDate, setJournalDate] = useState(yesterday)
  const [letterDate, setLetterDate] = useState(yesterday)
  const [artSci, setArtSci] = useState('')

  // The bird to re-draw, and the photograph to cut it from — a Wikimedia "File:Name", a
  // media-viewer link, or a direct image URL. Empty means "the lead photo, as before".
  const [illusSci, setIllusSci] = useState('')
  const [illusRef, setIllusRef] = useState('')

  // The place whose dúchas story to clear + re-source (a false hit from a same-named place elsewhere).
  const [lorePlace, setLorePlace] = useState('')

  // The Bluesky streams. Read on its own endpoint rather than carried on the health payload: it
  // costs the blog feed and a detection query per roster bird, and the overview polls. `bskyReads`
  // is bumped after a test post so the ledger below it is re-read rather than left stale.
  const [bsky, setBsky] = useState<Bsky | null>(null)
  const [bskyBusy, setBskyBusy] = useState(false)
  const [bskyReads, setBskyReads] = useState(0)
  const [bskyDate, setBskyDate] = useState(yesterday)

  // The keeper's note for the NEXT letter. No date to pick: a note is something you say
  // ahead, and the server decides which day that is from the station's own timezone.
  const [note, setNote] = useState('')

  // The workspace owns its own lifecycle now (no SidePanel): Escape closes it, and the page
  // behind is scroll-locked while it's up.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onEsc)
    document.body.classList.add('modal-open')
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.classList.remove('modal-open')
    }
  }, [onClose])

  useEffect(() => {
    let live = true
    fetch('/admin/note', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: { note: string | null }) => {
        if (live) setNote(j.note ?? '')
      })
      .catch(() => {
        if (live) setNote('')
      })
    return () => {
      live = false
    }
  }, [])

  // Only while the section is open: a preview renders the posts, which reaches the blog feed and
  // the station's own illustrations, and there is no reason to pay for that on the overview.
  useEffect(() => {
    if (section !== 'bluesky') return
    let live = true
    setBskyBusy(true)
    fetch(`/admin/bluesky?date=${encodeURIComponent(bskyDate)}`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: Bsky) => {
        if (live) setBsky(j)
      })
      .catch(() => {
        if (live) setBsky(null)
      })
      .finally(() => {
        if (live) setBskyBusy(false)
      })
    return () => {
      live = false
    }
  }, [section, bskyDate, bskyReads])

  // Drive the empty states off "have I data / am I fetching" rather than react-query's status
  // flags: a failed fetch can settle as paused-pending rather than error, which left the
  // console rendering nothing at all. No state here shows a blank panel.
  const forbidden = String((error as Error | null)?.message ?? '').startsWith('403')

  // Returns the server's result so a caller can react to it (the broadcast unlocks its send
  // only once a test copy has actually gone).
  const mutate = (url: string, method: string, body?: object): Promise<Result | null> => {
    setBusy(true)
    setResult(null)
    return fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf() },
      body: body ? JSON.stringify(body) : undefined,
    })
      .then(async (r) => {
        const j = (await r.json().catch(() => null)) as Result | null
        const outcome = j ?? { ok: r.ok, message: r.ok ? 'Done.' : `Error ${r.status}.` }
        setResult(outcome)
        qc.invalidateQueries({ queryKey: ['health'] })
        return outcome
      })
      .catch(() => {
        setResult({ ok: false, message: 'Request failed.' })
        return null
      })
      .finally(() => setBusy(false))
  }

  // Any edit after a test send invalidates it — otherwise you could test one message and
  // broadcast another.
  const editBlast = (change: () => void) => {
    change()
    setBlastTested(false)
    setBlastHtml(null)
    setBlastConfirm('')
  }

  // One deliberate test post, through the stream's own entry point on the server. The ledger is
  // re-read afterwards, because what the ledger says happened is the answer — the stream's own
  // return value only means "this subject is settled".
  const postStream = (name: BskyStream['name'], repost: boolean) =>
    mutate('/admin/bluesky/post', 'POST', { stream: name, date: bskyDate, repost }).then(() =>
      setBskyReads((n) => n + 1),
    )

  const l = h?.listening
  // Defaulted rather than optional-chained everywhere: a cloud that hasn't been redeployed
  // alongside the device can serve a health payload with no device block at all, and the
  // console must degrade to "nothing reported" rather than to a blank panel.
  const d = h?.device ?? { reporting: 'none' as const }

  // The address mail goes out as. Which box actually runs the sweep is deployment trivia and
  // stays out of the copy — sends_here only decides whether a missing address is a FAULT
  // (it is on the station that mails; it's simply how things are on a laptop or the Pi).
  const delivery = (): { text: string; warn: boolean } => {
    if (!h) return { text: '—', warn: false }
    // A From on a domain the station can't send from (the admin's own address is the classic) is
    // shown as a fault BEFORE a send is tried — this is the exact SES-rejection cause, made visible.
    if (h.alerts.from_status === 'foreign_domain')
      return { text: `${h.alerts.from} — not the station's domain; SES will reject`, warn: true }
    if (h.alerts.configured) return { text: h.alerts.from ?? '—', warn: false }
    // Unset: a fault only where sending is expected (the cloud); by design on the Pi/dev.
    return { text: 'Not set — email off', warn: h.letter.sends_here }
  }

  const backup = (): { text: string; warn: boolean } => {
    if (!h) return { text: '—', warn: false }
    if (h.backup.configured) return { text: h.backup.bucket ?? '—', warn: false }
    return h.backup.expected ? { text: 'Not configured', warn: true } : { text: 'Not used here', warn: false }
  }

  return (
    <div className={adm.workspace} role="dialog" aria-modal="true" aria-label="Admin">
      <div className={adm.inner}>
        <div className={adm.header}>
          <div className={adm.headerLeft}>
            {onBack && (
              <QuietButton className={adm.back} onClick={onBack}>
                ← Account
              </QuietButton>
            )}
            <h1 className={adm.title}>Admin</h1>
          </div>
          <CloseButton aria-label="Close" onClick={onClose} />
        </div>

        {result && <p className={result.ok ? adm.flash.ok : adm.flash.alert}>{result.message}</p>}

        {!h && isFetching && <p className={adm.lead}>Reading the station…</p>}

        {!h && !isFetching && (
          <section>
            <p className={adm.flash.alert}>
              {forbidden
                ? 'This account does not have admin access on this station.'
                : 'Could not read the station health.'}
            </p>
            {!forbidden && (
              // resetQueries, not refetch: a query that failed can settle into a state where
              // refetch is a no-op, which left the console stuck here until a full page reload.
              <Button variant="ghost" size="sm" onClick={() => qc.resetQueries({ queryKey: ['health'] })}>
                Try again
              </Button>
            )}
          </section>
        )}

        {h && l && (
          <div className={adm.console_}>
            <nav className={adm.nav} aria-label="Admin sections">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  className={section === s.id ? adm.navItem.on : adm.navItem.off}
                  aria-current={section === s.id ? 'true' : undefined}
                  onClick={() => setSection(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </nav>

            <div className={adm.pane}>
              {section === 'overview' && (
                <>
                  <h2 className={adm.paneTitle}>Overview</h2>
                  <p className={adm.lead}>
                    <span className={adm.dot[l.freshness as keyof typeof adm.dot] ?? adm.dot.none} />
                    <span>{STATUS[l.freshness] ?? 'Unknown'}</span>
                    {l.last_alive_at && <span className={adm.abs}>{stamp(l.last_alive_at)}</span>}
                  </p>
                  {/* ago() already carries its own "ago" ("3h ago" / "now") — don't add another. */}
                  {l.last_heard_at && (
                    <p className={adm.noteLine}>
                      Last heard {ago(l.last_heard_at)}
                      {l.last_species ? ` — ${l.last_species.en}` : ''}.
                    </p>
                  )}

                  {/* Silent failures — the whole reason this feed exists: a per-day scene that wouldn't
                      render, a bounced send. Shown only when something is actually wrong; a healthy
                      station has no Problems block at all. */}
                  {h.faults.length > 0 && (
                    <div className={adm.faults} role="alert">
                      <p className={adm.faultsHead}>
                        Problems <span className={adm.faultsN}>{h.faults.length}</span>
                      </p>
                      <ul className={adm.faultsList}>
                        {h.faults.map((f) => (
                          <li key={f.area} className={adm.fault}>
                            <span className={adm.faultArea}>
                              {faultLabel(f.area)}
                              {f.count > 1 && <span className={adm.faultCount}> ×{f.count}</span>}
                              {f.at && <span className={adm.abs}> {ago(f.at)}</span>}
                            </span>
                            <span className={adm.faultMsg}>
                              {f.message}
                              {f.context ? ` (${f.context})` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className={adm.stats}>
                    <div className={adm.stat}>
                      <span className={adm.fig}>{l.detections_today.toLocaleString()}</span>
                      <span className={adm.lbl}>Detections today</span>
                    </div>
                    <div className={adm.stat}>
                      <span className={adm.fig}>{l.species_today}</span>
                      <span className={adm.lbl}>Species today</span>
                    </div>
                  </div>

                  <ul className={adm.rules}>
                    <li className={adm.rule}>
                      <span className={adm.ruleLabel}>Email delivery</span>
                      <span className={delivery().warn ? `${adm.ruleValue} ${adm.warn}` : adm.ruleValue}>
                        {delivery().text}
                      </span>
                    </li>
                    <li className={adm.rule}>
                      <span className={adm.ruleLabel}>Offsite backup</span>
                      <span className={backup().warn ? `${adm.ruleValue} ${adm.warn}` : adm.ruleValue}>
                        {backup().text}
                      </span>
                    </li>
                    <li className={adm.rule}>
                      <span className={adm.ruleLabel}>Alerts waiting to send</span>
                      <span
                        className={
                          h.alerts.events_pending > 0 ? `${adm.ruleValue} ${adm.warn}` : adm.ruleValue
                        }
                      >
                        {h.alerts.events_pending}
                      </span>
                    </li>
                  </ul>

                  {l.restartable && (
                    <div className={`${adm.field} ${adm.mt}`}>
                      <p className={adm.fieldLabel}>Microphone listener</p>
                      <p className={adm.fieldHint}>Restart it if the station has stopped hearing anything.</p>
                      <div className={adm.control}>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => mutate('/admin/birdnet/restart', 'POST')}
                        >
                          Restart the microphone listener
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {section === 'device' && (
                <>
                  <h2 className={adm.paneTitle}>Device</h2>
                  {/* Everything in this section is a REPORT from the wall, not something the
                      cloud knows. So the first thing said is when it was made — a station that
                      stopped reporting must never be able to look like one reporting good news. */}
                  {d.reporting === 'none' ? (
                    <p className={adm.lead}>
                      The station has not reported on itself yet &mdash; not once, so this is not a stale
                      reading. Vitals ride on the cloud push rather than a job of their own, so the things to
                      check on the device are whether <code>ealta-push.timer</code> is enabled and whether it
                      is running code new enough to collect them (<code>birdnet/vitals.py</code>). The
                      runbook&rsquo;s failure playbook has the three commands. The cloud mirror has nothing of
                      its own to show here.
                    </p>
                  ) : (
                    <>
                      <p className={adm.lead}>
                        <span className={d.reporting === 'fresh' ? adm.dot.fresh : adm.dot.stale} />
                        <span>
                          {d.reporting === 'fresh' ? 'Reporting' : 'Last reported'}{' '}
                          {ago(d.received_at ?? null)}
                        </span>
                        {d.received_at && <span className={adm.abs}>{stamp(d.received_at)}</span>}
                      </p>

                      {/* Already sentences from the server — the device decodes its own bitfields,
                          so nothing here is ever a hex code to look up. */}
                      {(d.warnings ?? []).map((w) => (
                        <p key={w} className={adm.flash.alert}>
                          {w}
                        </p>
                      ))}

                      <ul className={adm.rules}>
                        <Vital
                          label="Panel last refreshed"
                          value={d.panel?.pushed_at ? ago(d.panel.pushed_at) : 'Never'}
                          warn={!d.panel?.pushed_at}
                        />
                        <Vital
                          label="Panel last checked"
                          value={
                            d.panel?.ran_at
                              ? `${ago(d.panel.ran_at)} — ${PANEL_OUTCOME[d.panel.outcome ?? ''] ?? 'unknown'}`
                              : null
                          }
                          warn={d.panel?.outcome === 'failed'}
                        />
                        <Vital
                          label="Version"
                          value={
                            d.version?.sha
                              ? `${d.version.sha}${d.version.dirty ? ' (edited on the box)' : ''}`
                              : null
                          }
                          warn={d.version?.dirty === true}
                        />
                        <Vital label="Up for" value={formatDuration(d.uptime ?? null)} />
                        {/* The device's own clock at collection, against ours at receipt — shown
                            ONLY when the two disagree. A matching clock is the normal case and
                            would just be another dashed row to read past. */}
                        {clockSaid(d.reported_at, d.received_at) && (
                          <Vital
                            label="Its clock said"
                            value={clockSaid(d.reported_at, d.received_at)}
                            warn
                          />
                        )}
                        <Vital
                          label="Offsite backup reached"
                          value={
                            d.litestream?.error
                              ? d.litestream.error
                              : d.litestream?.at
                                ? ago(d.litestream.at)
                                : null
                          }
                          warn={!!d.litestream?.error}
                        />
                        <Vital label="Card space free" value={diskFree(d.disk)} />
                        {/* One decimal always: the Pi reports a float, so 44.2 and 51.0 would
                            otherwise print as "44.2°C" and "51°C" in the same column. */}
                        <Vital
                          label="Temperature"
                          value={d.cpu_temp_c != null ? `${d.cpu_temp_c.toFixed(1)}°C` : null}
                        />
                        {/* Power reads as words, never a throttle code. Unknown (no vcgencmd, i.e.
                            not a Pi) is left blank rather than claimed as fine. */}
                        <Vital
                          label="Power"
                          value={
                            d.power?.now
                              ? 'Sagging now'
                              : d.power?.since_boot
                                ? 'Sagged since boot'
                                : d.power?.since_boot === false
                                  ? 'Steady'
                                  : null
                          }
                          warn={!!d.power?.now || !!d.power?.since_boot}
                        />
                        <Vital label="Microphone" value={d.mic_name ?? null} />
                      </ul>

                      {d.services && (
                        <ul className={adm.rules}>
                          {Object.entries(d.services).map(([name, unit]) => (
                            <Vital
                              key={name}
                              label={name}
                              // Restarts are shown alongside the state because the state alone
                              // hides a crash loop: "active" on its two-hundredth start reads
                              // exactly like "active" on its first.
                              value={`${unit.state ?? 'unknown'}${unit.restarts ? ` · ${unit.restarts} restarts` : ''}`}
                              warn={unit.state !== 'active'}
                            />
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </>
              )}

              {section === 'content' && (
                <>
                  <h2 className={adm.paneTitle}>Content</h2>
                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>Species descriptions</p>
                    <p className={adm.fieldHint}>
                      Rebuild every bird&rsquo;s description. Runs in the background, a bird at a time.
                    </p>
                    <div className={adm.control}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => mutate('/admin/species/refresh', 'POST', { refresh: true })}
                      >
                        Rebuild descriptions
                      </Button>
                    </div>
                  </div>

                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>Local lore</p>
                    <p className={adm.fieldHint}>
                      Clear a place&rsquo;s d&uacute;chas story and re-source it &mdash; the fix for a false
                      hit (a same-named place in another county). It drops from the journal at once, and the
                      sweep refetches within a couple of hours against the corrected note.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        type="text"
                        value={lorePlace}
                        onChange={(e) => setLorePlace(e.target.value)}
                        placeholder="Rosroe"
                        aria-label="place to re-source"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !lorePlace}
                        onClick={() => mutate('/admin/lore/resource', 'POST', { place: lorePlace })}
                      >
                        Re-source place lore
                      </Button>
                    </div>
                  </div>

                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>A note for the next letter</p>
                    <p className={adm.fieldHint}>
                      A line in your own voice, carried by the letter going out at {h.letter.at} and shown on
                      that day&rsquo;s journal entry. Say what is coming, not what has been. Leave it empty to
                      remove one.
                    </p>
                    <Textarea
                      className={adm.noteBox}
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="The feeders are down for repairs tomorrow, so the garden may be quiet."
                      aria-label="note for the next letter"
                    />
                    <div className={adm.control}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => mutate('/admin/note', 'PUT', { note })}
                      >
                        Save note
                      </Button>
                    </div>
                  </div>

                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>Journal</p>
                    <p className={adm.fieldHint}>
                      Rebuild a completed day&rsquo;s journal. The letter is the same words.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        type="date"
                        value={journalDate}
                        onChange={(e) => setJournalDate(e.target.value)}
                        aria-label="journal date to rebuild"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !journalDate}
                        onClick={() => mutate('/admin/journal/regenerate', 'POST', { date: journalDate })}
                      >
                        Rebuild journal
                      </Button>
                    </div>
                  </div>

                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>Art &amp; archaeology plate</p>
                    <p className={adm.fieldHint}>
                      Re-render one bird&rsquo;s cited plate (bird_art.yml) after a prompt or seed change. By
                      species &mdash; a plate is the same for every day that bird leads.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        value={artSci}
                        onChange={(e) => setArtSci(e.target.value)}
                        placeholder="Ardea cinerea"
                        aria-label="species (scientific name) whose plate to re-render"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !artSci.trim()}
                        onClick={() => mutate('/admin/bird-art/regenerate', 'POST', { sci: artSci.trim() })}
                      >
                        Regenerate plate
                      </Button>
                    </div>
                  </div>

                  {h.art.regenerable && (
                    <div className={adm.field}>
                      <p className={adm.fieldLabel}>Bird illustration</p>
                      <p className={adm.fieldHint}>
                        Re-draw one bird&rsquo;s collage linocut &mdash; the picture on the wall, the
                        catalogue and the journal. The pipeline cuts from the Wikipedia lead photo by default,
                        which is sometimes the wrong plate: a bird puffed round against the cold makes a fine
                        photograph and a fuzzy linocut. Name a better one and it is cut from that instead.
                        Leave it empty to simply draw the bird again.
                      </p>
                      <div className={adm.control}>
                        <Field
                          className={adm.inputInline}
                          value={illusSci}
                          onChange={(e) => setIllusSci(e.target.value)}
                          placeholder="Aegithalos caudatus"
                          aria-label="species (scientific name) whose illustration to re-draw"
                        />
                        <Field
                          className={adm.inputInline}
                          value={illusRef}
                          onChange={(e) => setIllusRef(e.target.value)}
                          placeholder="File:Long-tailed_tit.jpg (optional)"
                          aria-label="source photograph — a Wikimedia File: name, a media-viewer link, or a direct image URL"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy || !illusSci.trim()}
                          onClick={() =>
                            mutate('/admin/illustration/regenerate', 'POST', {
                              sci: illusSci.trim(),
                              ref: illusRef.trim(),
                            })
                          }
                        >
                          Re-draw bird
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>Correct a detection</p>
                    <p className={adm.fieldHint}>
                      Fix a single mis-identified detection by its id. Relabel it to the right species, or
                      remove it outright when it was nothing the label could name (wind, a door). One row — a
                      whole bad species is the Danger zone&rsquo;s job.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        inputMode="numeric"
                        value={detId}
                        onChange={(e) => setDetId(e.target.value)}
                        placeholder="detection id"
                        aria-label="detection id to correct"
                      />
                      <Field
                        className={adm.inputInline}
                        value={detSpecies}
                        onChange={(e) => setDetSpecies(e.target.value)}
                        placeholder="Erithacus rubecula"
                        aria-label="scientific name to relabel to"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !detId.trim() || !detSpecies.trim()}
                        onClick={() =>
                          mutate(`/admin/detections/${detId.trim()}`, 'PATCH', {
                            sci_name: detSpecies.trim(),
                          })
                        }
                      >
                        Relabel
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={busy || !detId.trim()}
                        onClick={() => mutate(`/admin/detections/${detId.trim()}`, 'DELETE')}
                      >
                        Remove detection
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {section === 'email' && (
                <>
                  <h2 className={adm.paneTitle}>Email</h2>
                  <ul className={adm.rules}>
                    <li className={adm.rule}>
                      <span className={adm.ruleLabel}>Goes out</span>
                      <span className={adm.ruleValue}>
                        Every day at {h.letter.at} {h.letter.zone}
                      </span>
                    </li>
                    <li className={adm.rule}>
                      <span className={adm.ruleLabel}>Sending as</span>
                      <span className={delivery().warn ? `${adm.ruleValue} ${adm.warn}` : adm.ruleValue}>
                        {delivery().text}
                      </span>
                    </li>
                  </ul>

                  <div className={`${adm.field} ${adm.mt}`}>
                    <p className={adm.fieldLabel}>A day&rsquo;s letter</p>
                    <p className={adm.fieldHint}>
                      Read it as a subscriber would, or send yourself a copy. Subscribers are only ever mailed
                      by the schedule above.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        type="date"
                        value={letterDate}
                        onChange={(e) => setLetterDate(e.target.value)}
                        aria-label="letter date"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!letterDate}
                        onClick={() =>
                          window.open(
                            `/admin/letter/preview?date=${encodeURIComponent(letterDate)}`,
                            '_blank',
                            'noopener',
                          )
                        }
                      >
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !letterDate || !h.alerts.configured}
                        onClick={() => mutate('/admin/letter/test', 'POST', { date: letterDate })}
                      >
                        Send to me
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {section === 'bluesky' && (
                <>
                  <h2 className={adm.paneTitle}>Bluesky</h2>
                  <p className={adm.lead}>
                    Three streams, one account. Each posts only when its credentials are present AND its own
                    switch is on, so read what a stream would say before switching it on. Nothing on this page
                    publishes anything &mdash; the previews are rendered and thrown away.
                  </p>

                  {!bsky && bskyBusy && <p className={adm.noteLine}>Reading the streams&hellip;</p>}
                  {!bsky && !bskyBusy && <p className={adm.flash.alert}>Could not read the streams.</p>}

                  {bsky && (
                    <>
                      <ul className={adm.rules}>
                        <li className={adm.rule}>
                          <span className={adm.ruleLabel}>Credentials</span>
                          <span
                            className={
                              credentialsSaid(bsky.credentials).warn
                                ? `${adm.ruleValue} ${adm.warn}`
                                : adm.ruleValue
                            }
                          >
                            {credentialsSaid(bsky.credentials).text}
                          </span>
                        </li>
                      </ul>

                      {bsky.streams.map((stream) => {
                        const copy = STREAM_COPY[stream.name]
                        const said = gateSaid(stream)
                        return (
                          <div key={stream.name} className={adm.field}>
                            <p className={adm.fieldLabel}>{copy.label}</p>
                            <p className={adm.fieldHint}>{copy.hint}</p>
                            <ul className={adm.rules}>
                              <li className={adm.rule}>
                                <span className={adm.ruleLabel}>Stream</span>
                                <span className={said.on ? adm.ruleValue : `${adm.ruleValue} ${adm.warn}`}>
                                  {said.text}
                                </span>
                              </li>
                            </ul>

                            {/* Arrivals is a roster, so the preview is per bird — and the roster itself
                                is the picker, which is why there is no box to type a species into. */}
                            {stream.name === 'arrivals' ? (
                              <>
                                {stream.baseline === false && (
                                  <p className={adm.flash.alert}>
                                    The station does not hold {stream.absence_days} days of its own listening
                                    yet, so no silence here means anything and the scan would post nothing
                                    whatever the roster below says.
                                  </p>
                                )}
                                {(stream.roster ?? []).length === 0 && (
                                  <span className={adm.empty}>
                                    No birds on the roster (bluesky.arrivals.species).
                                  </span>
                                )}
                                {(stream.roster ?? []).map((bird) => (
                                  <div key={bird.sci} className={adm.mt}>
                                    <p className={adm.fieldLabel}>
                                      {bird.en}
                                      {bird.ga ? ` · ${bird.ga}` : ''}
                                    </p>
                                    <p className={adm.fieldHint}>
                                      {bird.reason ?? 'Arriving today — this is what would go out.'}{' '}
                                      {bird.last_heard
                                        ? `Last heard before today: ${bird.last_heard}.`
                                        : 'Never heard here before.'}
                                    </p>
                                    {bird.preview && <Post preview={bird.preview} />}
                                  </div>
                                ))}
                              </>
                            ) : stream.preview ? (
                              <>
                                {stream.preview.stale && (
                                  <p className={adm.flash.alert}>
                                    This note is more than a week old, so a run would record it as handled and
                                    post nothing. Only new writing is news.
                                  </p>
                                )}
                                <Post preview={stream.preview} />
                              </>
                            ) : (
                              <p className={adm.fieldHint}>{stream.reason}</p>
                            )}

                            <Ledger rows={stream.ledger} />

                            <div className={adm.control}>
                              {stream.name === 'daily' && (
                                <Field
                                  className={adm.inputInline}
                                  type="date"
                                  value={bskyDate}
                                  onChange={(e) => setBskyDate(e.target.value)}
                                  aria-label="journal date to preview"
                                />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy || !said.on}
                                onClick={() => postStream(stream.name, false)}
                              >
                                Post this now
                              </Button>
                              {/* Danger where the plain send is not: this one DELETES the ledger row
                                  that was keeping the subject settled, and then publishes. The notes
                                  stream has none — a note row is keyed by its slug, so dropping it
                                  would put the same writing in the feed a second time. */}
                              {stream.name !== 'notes' && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  disabled={busy || !said.on}
                                  onClick={() => postStream(stream.name, true)}
                                >
                                  Post again (drops the ledger row)
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )}
                </>
              )}

              {section === 'broadcast' && (
                <>
                  <h2 className={adm.paneTitle}>Broadcast</h2>
                  <div className={adm.field}>
                    <p className={adm.fieldLabel}>A one-off note to the letter&rsquo;s readers</p>
                    <p className={adm.fieldHint}>
                      For the times something needs saying — the station off for a fortnight, say. It goes to
                      the {h.letter.readers} {h.letter.readers === 1 ? 'person' : 'people'} on the daily
                      letter, and nobody else. Read it, send yourself a copy, then confirm.
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputFull}
                        value={blastSubject}
                        onChange={(e) => editBlast(() => setBlastSubject(e.target.value))}
                        placeholder="Subject"
                        aria-label="broadcast subject"
                      />
                    </div>
                    <Textarea
                      className={adm.noteBox}
                      rows={5}
                      value={blastBody}
                      onChange={(e) => editBlast(() => setBlastBody(e.target.value))}
                      placeholder="The station is off for a fortnight while the mic is repaired. The journal will pick up again when it returns."
                      aria-label="broadcast body"
                    />
                    <div className={adm.control}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !blastSubject.trim() || !blastBody.trim()}
                        onClick={() =>
                          fetch('/admin/blast/preview', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Accept: 'application/json',
                              'X-CSRF-Token': csrf(),
                            },
                            body: JSON.stringify({ subject: blastSubject, body: blastBody }),
                          })
                            .then((r) => r.json())
                            .then((j) => setBlastHtml(j.html ?? null))
                            .catch(() => setResult({ ok: false, message: 'Could not render the preview.' }))
                        }
                      >
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !blastSubject.trim() || !blastBody.trim() || !h.alerts.configured}
                        onClick={() =>
                          mutate('/admin/blast/test', 'POST', {
                            subject: blastSubject,
                            body: blastBody,
                          }).then((r) => setBlastTested(!!r?.ok))
                        }
                      >
                        Send to me
                      </Button>
                    </div>
                  </div>

                  {blastHtml && (
                    <div className={adm.field}>
                      <p className={adm.fieldLabel}>What they will get</p>
                      {/* srcDoc, not innerHTML: the email's own markup is rendered in an isolated
                          document so its styles can't leak into the console. */}
                      <iframe className={adm.blastPreview} title="Broadcast preview" srcDoc={blastHtml} />
                    </div>
                  )}

                  <div className={`${adm.zone} ${adm.mt}`}>
                    <p className={adm.zoneH}>Send to everyone</p>
                    <p className={adm.fieldHint}>
                      {blastTested
                        ? `Type ${h.letter.readers} to confirm. This cannot be unsent.`
                        : 'Send yourself a copy first — the send unlocks once one has landed.'}
                    </p>
                    <div className={adm.control}>
                      <Field
                        className={adm.inputInline}
                        value={blastConfirm}
                        onChange={(e) => setBlastConfirm(e.target.value)}
                        placeholder={String(h.letter.readers)}
                        disabled={!blastTested}
                        aria-label="type the reader count to confirm"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={
                          busy ||
                          !blastTested ||
                          blastConfirm.trim() !== String(h.letter.readers) ||
                          !blastSubject.trim() ||
                          !blastBody.trim()
                        }
                        onClick={() =>
                          mutate('/admin/blast', 'POST', {
                            subject: blastSubject,
                            body: blastBody,
                            confirm: blastConfirm,
                          }).then((r) => {
                            if (r?.ok) editBlast(() => {})
                          })
                        }
                      >
                        Send to {h.letter.readers}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {section === 'danger' && (
                <div className={adm.zone}>
                  <p className={adm.zoneH}>Danger zone</p>

                  <p className={adm.fieldLabel}>Remove a species</p>
                  <p className={adm.fieldHint}>
                    Deletes every detection for a species and any alert it raised — for a BirdNET false
                    positive the station can’t have heard (a Common Crane in Connemara). Other species are
                    untouched. Comma-separate to remove several. Preview first to see the counts. This cannot
                    be undone.
                  </p>
                  <div className={adm.control}>
                    <Field
                      className={adm.inputInline}
                      value={purgeSpecies}
                      onChange={(e) => {
                        setPurgeSpecies(e.target.value)
                        setPurgeConfirm('')
                      }}
                      placeholder="Common Crane, Tawny Owl"
                      aria-label="species to remove, comma-separated"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy || !purgeSpecies.trim()}
                      onClick={() =>
                        mutate('/admin/detections/remove_species', 'POST', { species: purgeSpecies })
                      }
                    >
                      Preview
                    </Button>
                  </div>
                  <div className={adm.control}>
                    <Field
                      className={adm.inputInline}
                      value={purgeConfirm}
                      onChange={(e) => setPurgeConfirm(e.target.value)}
                      placeholder="DELETE"
                      aria-label="type DELETE to confirm removing the species"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy || !purgeSpecies.trim() || purgeConfirm !== 'DELETE'}
                      onClick={() =>
                        mutate('/admin/detections/remove_species', 'POST', {
                          species: purgeSpecies,
                          confirm: purgeConfirm,
                        })
                      }
                    >
                      Remove species
                    </Button>
                  </div>

                  <p className={`${adm.fieldLabel} ${adm.mt}`}>Clear the detection history</p>
                  <p className={adm.fieldHint}>
                    Removes every detection and everything derived from it. Species artwork and descriptions
                    are kept. This cannot be undone.
                  </p>
                  <div className={adm.control}>
                    <Field
                      className={adm.inputInline}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="DELETE"
                      aria-label="type DELETE to confirm"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy || confirm !== 'DELETE'}
                      onClick={() => mutate('/admin/data', 'DELETE', { confirm })}
                    >
                      Clear history
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
